import {
  Avatar,
  Badge,
  Box,
  Divider,
  Flex,
  Icon,
  IconButton,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { OrderFormElementRoute, OrderStatus } from "enums";
import { ORDER_STATUS_COLOR } from "modules/orders";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Card, WarningDialog } from "shared";
import { MedsAndIvModal } from "./MedsAndIvModals";
import { toDisplay } from "utils/enum-mapper";
import { useFormContext } from "react-hook-form";
import { TriageIVMedicationSectionByType } from "modules/charts-shared";
import { EmrContrastTriangle, SiPlus } from "@medstonetech/slate-icons";
import { usePermissions } from "contexts";
import type { EditingLock } from "../../hooks/useRealtimeIvMedications";

type IvAndMedicationsCardProps = {
  item: TriageIVMedicationSectionByType;
  onSave: () => void | Promise<void>;
  isReadOnly?: boolean;
  editingLocks: Record<string, EditingLock>;
  sendEditingState: (orderId: string, isEditing: boolean) => void;
};

type IvAndMedicationsTimerProps = {
  type: string;
  item: TriageIVMedicationSectionByType;
  onSave?: () => void | Promise<void>;
  isReadOnly?: boolean;
};

function IvAndMedicationsTimer(props: IvAndMedicationsTimerProps) {
  const { item, type, onSave, isReadOnly } = props;
  const { setValue } = useFormContext();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [timerMs, setTimerMs] = useState<number>(0);
  const [timerDate, setTimerDate] = useState<Date | null>(null);
  const [timeDifference, setTimeDifference] = useState<number>(0);
  const hasAutoUpdatedRef = useRef(false);

  useEffect(() => {
    hasAutoUpdatedRef.current = false;
  }, [item.orderId, item.order?.scheduledTime, type]);

  useEffect(() => {
    if (item.scheduled !== "Scheduled") {
      return;
    }

    const autoTransitionToOrdered = () => {
      if (isReadOnly) {
        return;
      }

      if (hasAutoUpdatedRef.current) {
        return;
      }

      const finalizableStatuses = [
        "Prepared",
        "InProcess",
        "Completed",
        "Administered",
      ];
      if (finalizableStatuses.includes(item.status as string)) {
        return;
      }

      hasAutoUpdatedRef.current = true;
      setValue(`${type}.${item.orderId}.scheduled`, "Ordered");

      if (item.status === "PRN") {
        setValue(`${type}.${item.orderId}.status`, "Ordered");
      }
    };

    if (!item.order?.scheduledTime) {
      autoTransitionToOrdered();
      return;
    }

    const updateTimer = () => {
      const now = new Date();
      const date = new Date(item.order?.scheduledTime as Date);
      const difference = date.getTime() - now.getTime();

      setTimerDate(date);
      setTimeDifference(difference);
      setTimerMs(difference > 0 ? difference : 0);

      if (difference <= 0 && item.scheduled === "Scheduled") {
        autoTransitionToOrdered();
      }
    };

    updateTimer();

    const interval = setInterval(() => {
      updateTimer();
    }, 1000);

    return () => clearInterval(interval);
  }, [
    item.order,
    item.orderId,
    item.scheduled,
    item.status,
    item.order?.scheduledTime,
    setValue,
    type,
    isReadOnly,
    onSave,
  ]);

  const timerVal = useMemo(
    () => new Date(timerMs).toISOString().substr(11, 5),
    [timerMs]
  );

  return (
    <Box
      sx={{
        gap: 4,
        display: "flex",
      }}
    >
      {timerDate && timerMs > 0 && (
        <Badge
          bg={timeDifference > 0 ? "red" : "green"}
          color="white"
          borderRadius="20px"
          padding="0.125rem 0.5rem"
        >
          <Text fontSize="xs">{timerVal}</Text>
        </Badge>
      )}

      {item.status === "PRN" && timeDifference <= 0 && (
        <IconButton
          aria-label="Add IV/Medication"
          icon={
            <Icon
              borderRadius="1rem"
              color="white"
              backgroundColor="green"
              as={SiPlus}
            />
          }
          color="blue"
          variant="icon"
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
          isDisabled={timeDifference > 0 || isReadOnly}
        />
      )}

      <WarningDialog
        isOpen={isOpen}
        mainText="Are you sure you want to mark this order as ready?"
        title="Warning!"
        actionLabel="Ready"
        onCancel={onClose}
        onAction={() => {
          setValue(`${type}.${item.orderId}.status`, "Ordered");
          setValue(`${type}.${item.orderId}.scheduled`, "Ordered");

          onClose();
          onSave?.();
        }}
        onClose={onClose}
      />
    </Box>
  );
}

type IvMedicationsCardContentProps = {
  item: TriageIVMedicationSectionByType;
  onClick?: () => void;
  onSave?: () => void | Promise<void>;
  isReadOnly?: boolean;
  liveStatus?: string;
  liveScheduled?: string;
  editingLock?: EditingLock;
};

export function IvMedicationsCardContent({
  item,
  onClick,
  onSave,
  isReadOnly,
  liveStatus,
  liveScheduled,
  editingLock,
}: IvMedicationsCardContentProps) {
  const currentStatus = (liveStatus as string | undefined) ?? item.status;
  const currentScheduled =
    (liveScheduled as string | undefined) ?? item.scheduled;
  const medicationDisplay = (item.medication as string) ?? "";
  const solutionDisplay =
    typeof item.solution === "string"
      ? item.solution
          .replace("SNS", "S&S")
          .replace((item.route as string) ?? "", "")
      : "";

  const borderColor = editingLock
    ? editingLock.userColor
    : currentStatus
    ? ORDER_STATUS_COLOR[currentStatus as OrderStatus]
    : "gray.700";

  const isLocked = Boolean(editingLock);
  const tooltipLabel = editingLock
    ? `${editingLock.userName} is updating this medication`
    : undefined;

  const cardElement = (
    <Card
      w="100%"
      p="10px 5px"
      variant="basic"
      borderRight="8px solid"
      borderColor={borderColor}
      sx={isReadOnly ? { opacity: 0.6 } : {}}
      onClick={onClick}
      cursor={isLocked ? "not-allowed" : isReadOnly ? "default" : "pointer"}
      position="relative"
    >
      <Flex justify="space-between" mb={2}>
        {item.order?.isContrast && (
          <Flex align="center" mr={2}>
            <Icon color="yellow" as={EmrContrastTriangle} />
          </Flex>
        )}
        <Flex
          align="center"
          mr={2}
          width={item.order?.isContrast ? "90%" : "100%"}
          justifyContent="space-between"
        >
          <Text color="gray">
            {`${toDisplay(
              "orderFormElementRoute",
              (item.route as OrderFormElementRoute) ?? "PO"
            )} ${item.order?.isPRN ? " - PRN" : ""}`}
          </Text>
          <Text color="gray"></Text>

          {(currentStatus === "Ordered" || currentStatus === "Prepared") && (
            <>
              {currentScheduled === "Ordered" && (
                <Badge
                  textTransform="capitalize"
                  py=".15rem"
                  px=".5rem"
                  bg={currentStatus === "Ordered" ? "blue" : "cyan"}
                  color="white"
                  borderRadius="20px"
                >
                  {currentStatus === "Ordered" ? "Needs Prep" : "Preparing"}
                </Badge>
              )}
              {currentScheduled === "Scheduled" && (
                <IvAndMedicationsTimer
                  item={item}
                  type={item.type}
                  onSave={onSave}
                  isReadOnly={isReadOnly}
                />
              )}
            </>
          )}
          {currentStatus === "PRN" && (
            <IvAndMedicationsTimer
              item={item}
              type={item.type}
              onSave={onSave}
            />
          )}
        </Flex>
        {editingLock && (
          <Avatar
            size="xs"
            name={editingLock.userName}
            bg={editingLock.userColor}
            ml={2}
          />
        )}
      </Flex>
      <Flex align="center" mr={2}>
        <Text fontWeight="bold">{`${medicationDisplay} ${solutionDisplay}`}</Text>
      </Flex>
    </Card>
  );

  return (
    <Tooltip label={tooltipLabel} hasArrow isDisabled={!isLocked}>
      {cardElement}
    </Tooltip>
  );
}

function IvAndMedicationsCard({
  item,
  onSave,
  isReadOnly,
  editingLocks,
  sendEditingState,
}: IvAndMedicationsCardProps) {
  const disclosure = useDisclosure();
  const isOpenRef = useRef(disclosure.isOpen);
  isOpenRef.current = disclosure.isOpen;
  const { scope } = usePermissions();
  const { watch } = useFormContext();

  const liveStatus = item.orderId
    ? watch(`${item.type}.${item.orderId}.status`)
    : undefined;
  const liveScheduled = item.orderId
    ? watch(`${item.type}.${item.orderId}.scheduled`)
    : undefined;

  const title =
    item.route === "IV"
      ? "IV/Medication Infusion"
      : item.route === "IVP" || item.route === "IVPB"
      ? "IV Push"
      : "Medication";

  const isScheduledItem = (liveScheduled ?? item.scheduled) === "Scheduled";
  const editingLock = item.orderId ? editingLocks[item.orderId] : undefined;
  const isLockedByOther = Boolean(editingLock);

  const scopeIsReadOnly =
    (!item.order?.isContrast &&
      scope("inprocess:ivm:ivmnocontrast").isAccessible === false) ||
    (item.order?.isContrast &&
      scope("inprocess:ivm:ivmcontrast").isAccessible === false);

  useEffect(() => {
    if (
      disclosure.isOpen &&
      item.orderId &&
      !isReadOnly &&
      !isScheduledItem &&
      !scopeIsReadOnly
    ) {
      sendEditingState(item.orderId, true);
    }

    return () => {
      if (
        isOpenRef.current &&
        item.orderId &&
        !isReadOnly &&
        !isScheduledItem &&
        !scopeIsReadOnly
      ) {
        sendEditingState(item.orderId, false);
      }
    };
  }, [
    disclosure.isOpen,
    item.orderId,
    isReadOnly,
    isScheduledItem,
    scopeIsReadOnly,
    sendEditingState,
  ]);

  useEffect(() => {
    if (disclosure.isOpen && isLockedByOther) {
      disclosure.onClose();
    }
  }, [disclosure, isLockedByOther]);

  const handleClick = isLockedByOther ? undefined : disclosure.onOpen;

  return (
    <>
      <Box>
        <IvMedicationsCardContent
          item={item}
          onClick={handleClick}
          onSave={onSave}
          liveStatus={liveStatus}
          liveScheduled={liveScheduled}
          isReadOnly={isReadOnly || scopeIsReadOnly}
          editingLock={editingLock}
        />
      </Box>

      {disclosure.isOpen && (
        <MedsAndIvModal
          isOpen={disclosure.isOpen}
          onClose={disclosure.onClose}
          type={item.type}
          item={item}
          title={title}
          onSave={onSave}
          isReadOnly={isReadOnly || isScheduledItem || scopeIsReadOnly}
        />
      )}
    </>
  );
}

type IvAndMedicationsColumnProps = {
  header: ReactNode;
  status: OrderStatus;
  items: TriageIVMedicationSectionByType[];
  onSave: () => void | Promise<void>;
  isReadOnly?: boolean;
  editingLocks: Record<string, EditingLock>;
  sendEditingState: (orderId: string, isEditing: boolean) => void;
};

type IvAndMedicationsContentProps = Omit<IvAndMedicationsColumnProps, "header">;

type TriageIVMedicationSectionGrouped = {
  ordered: TriageIVMedicationSectionByType[];
  preparing: TriageIVMedicationSectionByType[];
  scheduled: TriageIVMedicationSectionByType[];
  prn: TriageIVMedicationSectionByType[];
};

function IvAndMedicationsContent({
  status,
  items,
  onSave,
  isReadOnly,
  editingLocks,
  sendEditingState,
}: IvAndMedicationsContentProps) {
  const groupedItems: TriageIVMedicationSectionGrouped =
    items.reduce<TriageIVMedicationSectionGrouped>(
      (acc, item) => {
        if (item.status === "PRN") {
          acc.prn.push(item);
        } else if (
          item.scheduled === "Scheduled" &&
          item.order?.scheduledTime
        ) {
          acc.scheduled.push(item);
        } else if (item.status === "Ordered") {
          acc.ordered.push(item);
        } else {
          acc.preparing.push(item);
        }
        return acc;
      },
      {
        ordered: [],
        preparing: [],
        scheduled: [],
        prn: [],
      }
    );

  return (
    <>
      {status === "Ordered"
        ? Object.keys(groupedItems).map((key) => {
            const itemList =
              groupedItems[key as keyof TriageIVMedicationSectionGrouped];

            return itemList.length > 0 ? (
              <Box
                key={key}
                sx={{
                  gap: 4,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    gap: 2,
                    display: "flex",
                    flexDirection: "column",
                    textAlign: "center",
                    justifyContent: "center",
                  }}
                >
                  <Text>
                    {
                      {
                        ordered: "Needs Prep",
                        preparing: "Preparing",
                        scheduled: "Scheduled",
                        prn: "PRN",
                      }[key]
                    }
                  </Text>
                  <Divider />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  {itemList.map((item) => (
                    <IvAndMedicationsCard
                      key={item.orderId}
                      item={item}
                      onSave={onSave}
                      isReadOnly={isReadOnly}
                      editingLocks={editingLocks}
                      sendEditingState={sendEditingState}
                    />
                  ))}
                </Box>
              </Box>
            ) : null;
          })
        : items.map((item) => (
            <IvAndMedicationsCard
              key={item.orderId}
              item={item}
              onSave={onSave}
              isReadOnly={isReadOnly}
              editingLocks={editingLocks}
              sendEditingState={sendEditingState}
            />
          ))}
    </>
  );
}

function IvAndMedicationsColumn({
  status,
  header,
  items,
  onSave,
  isReadOnly,
  editingLocks,
  sendEditingState,
}: IvAndMedicationsColumnProps) {
  return (
    <Box minW="24%" display="flex" flexDir="column" gap={4} px={2}>
      {header}

      <IvAndMedicationsContent
        status={status}
        items={items}
        onSave={onSave}
        isReadOnly={isReadOnly}
        editingLocks={editingLocks}
        sendEditingState={sendEditingState}
      />
    </Box>
  );
}

export { IvAndMedicationsColumn };
